import {centralISOFor} from './dateUtils.js';
import {exportDateValue} from './exportDates.js';

// An export row is an opaque database record: this module never knows which
// table it is serializing, only that a column names a key on the row or brings
// its own accessor. Every value goes through csvText() before it reaches the
// output, so `unknown` is both true and sufficient.
export type CsvRow = Record<string, unknown>;

export type CsvColumn<Row extends CsvRow = CsvRow> = {
  header?: string;
  key?: string;
  value?: (row: Row) => unknown;
};

function csvText(value: unknown): string {
  if (value == null) return '';
  // Table E item 23 ("CSV and print turn dates into full UTC timestamps"):
  // date values now render in the farm's Central time — YYYY-MM-DD, plus
  // HH:MM for true timestamps — so spreadsheet sorts stay correct. Detection
  // is strict (see exportDates); every other value keeps its text form.
  const date = exportDateValue(value);
  if (date !== null) return date;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function csvCell(value: unknown): string {
  let text = csvText(value).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // Neutralize spreadsheet formula/DDE injection: prefix a leading =,+,-,@,tab,
  // newline, or | (pipe — DDE in some locales) with a single quote.
  if (/^[=+\-@\t\n|]/.test(text)) text = "'" + text;
  if (/[",\n]/.test(text)) return '"' + text.replace(/"/g, '""') + '"';
  return text;
}

export function rowsToCsv<Row extends CsvRow>(
  columns: CsvColumn<Row>[] | null | undefined,
  rows: Row[] | null | undefined,
): string {
  const cols = Array.isArray(columns) ? columns : [];
  const list = Array.isArray(rows) ? rows : [];
  const lines: string[] = [];
  lines.push(cols.map((c) => csvCell(c.header || c.key || '')).join(','));
  for (const row of list) {
    lines.push(
      cols
        .map((c) => {
          if (typeof c.value === 'function') return csvCell(c.value(row));
          return csvCell(row && c.key ? row[c.key] : '');
        })
        .join(','),
    );
  }
  return lines.join('\r\n') + '\r\n';
}

export function csvFilename(prefix: string | null | undefined, date: Date = new Date()): string {
  const safePrefix = String(prefix || 'export')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const stamp = centralISOFor(date) || 'today';
  return (safePrefix || 'export') + '-' + stamp + '.csv';
}

// Returns false — rather than throwing — in any environment without the DOM
// download path (jsdom, a worker, SSR), which is the contract its callers rely
// on to fall back to a copy/print flow.
export function downloadCsv(filename: string, csv: string): boolean {
  if (
    typeof document === 'undefined' ||
    typeof Blob === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return false;
  }
  const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  if (typeof URL.revokeObjectURL === 'function') {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return true;
}
